import { datadogRum } from '@datadog/browser-rum';
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { TextField, UserAvatar } from '@side/pantry';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeFilterQueryValue } from '@/common/utils/sanitizeFilterQueryValue';
import { identityApi } from '$api-clients';
import type { IdentitySchema } from '$api-clients/generated';
import { useIdentity } from '$api-clients/hooks';
import { InactiveChip } from '../InactiveChip';

type UserStatus = IdentitySchema.components['schemas']['UserStatus'];

type UserOption = {
  id: string;
  displayName: string;
  avatar: string | null;
  status?: UserStatus | null;
};

function getNoOptionsText({
  isRestricted,
  hasInput,
}: {
  isRestricted: boolean;
  hasInput: boolean;
}) {
  if (isRestricted) return 'No matching team members';
  return hasInput ? 'No users found' : 'Type to search for users';
}

export function ViewAsDialog({
  open,
  onClose,
  onSelect,
  restrictToUserIds,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (userId: string) => void;
  restrictToUserIds?: string[];
}) {
  const isRestricted = restrictToUserIds !== undefined;
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);

  const { data: restrictedUsers, isLoading: restrictedLoading } = useIdentity(
    '/users',
    open && isRestricted && restrictToUserIds.length > 0
      ? {
          params: {
            query: {
              ids: restrictToUserIds.join(','),
              sort: 'displayName:ASC',
            },
          },
        }
      : null,
  );

  const restrictedOptions = useMemo<UserOption[]>(() => {
    if (!isRestricted) return [];
    return (restrictedUsers?.data ?? []).map((user) => ({
      id: user.id,
      displayName: user.displayName,
      avatar: user.photoUrl ?? null,
      status: user.status,
    }));
  }, [isRestricted, restrictedUsers]);

  const fetchUsers = useCallback(async (search: string) => {
    abortControllerRef.current?.abort();

    // Free text is interpolated bare into the q filter DSL; strip grammar
    // characters so typed `&`/`[` can't be parsed as operators. An empty or
    // all-structural value yields no request rather than an unbounded q.
    const sanitizedSearch = sanitizeFilterQueryValue(search);
    if (!sanitizedSearch) {
      setOptions([]);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const { data } = await identityApi.GET('/users', {
        params: {
          query: { q: sanitizedSearch },
        },
        signal: controller.signal,
      });
      setOptions(
        data?.data?.map((user) => ({
          id: user.id,
          displayName: user.displayName,
          avatar: user.photoUrl ?? null,
        })) ?? [],
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      datadogRum.addError(error);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const debouncedFetch = useMemo(() => debounce(fetchUsers, 300), [fetchUsers]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
      abortControllerRef.current?.abort();
    };
  }, [debouncedFetch]);

  const handleInputChange = (_: unknown, value: string) => {
    setInputValue(value);
    if (!isRestricted) {
      void debouncedFetch(value);
    }
  };

  const handleClose = () => {
    debouncedFetch.cancel();
    abortControllerRef.current?.abort();
    setInputValue('');
    setOptions([]);
    onClose();
  };

  const normalizedInput = inputValue.trim().toLowerCase();
  const displayedOptions = isRestricted
    ? restrictedOptions.filter((option) =>
        option.displayName.toLowerCase().includes(normalizedInput),
      )
    : options;
  const isLoading = isRestricted ? restrictedLoading : loading;
  const noOptionsText = getNoOptionsText({
    isRestricted,
    hasInput: inputValue.trim().length > 0,
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>View As</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={displayedOptions}
          loading={isLoading}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.displayName}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={noOptionsText}
          onChange={(_, value) => {
            if (value) {
              onSelect(value.id);
              handleClose();
            }
          }}
          renderOption={(props, option) => (
            <ListItem
              {...props}
              key={option.id}
              secondaryAction={
                isRestricted &&
                option.status != null &&
                option.status !== 'ACTIVE' && <InactiveChip />
              }
            >
              <ListItemAvatar>
                <UserAvatar
                  sx={{ width: 32, height: 32, fontSize: 14 }}
                  avatarUrl={option.avatar ?? undefined}
                  name={option.displayName}
                />
              </ListItemAvatar>
              <ListItemText primary={option.displayName} />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder={
                isRestricted
                  ? 'Search team members by name...'
                  : 'Search users by name or email...'
              }
              variant="outlined"
              sx={{ mt: 1 }}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps?.input,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress sx={{ color: 'inherit' }} size={20} />
                      ) : null}
                      {params.slotProps?.input?.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </DialogContent>
    </Dialog>
  );
}
